import { useAuthStore } from '@/modules/auth';

/**
 * Hook to check if a specific flag is enabled
 * @param flagName - The name of the flag to check
 * @returns boolean indicating if the flag is enabled
 */
export function useUserFlag(flagName: string): boolean {
  const flags = useAuthStore((state) => state.flags);

  if (!flags) {
    return false;
  }

  return flags.some((flag) => flag.code === flagName);
}

/**
 * Hook to check if user has the new platform flag enabled
 * @returns boolean indicating if the new platform is enabled for this user
 */
export function useUserHasNewPlatformAccess(): boolean {
  return useUserFlag('new_platform');
}

/**
 * Whether satellite-derived analytics are available: drawing and saving areas of
 * interest, the zonal-stats grid, the regional layer group and the wide-area
 * story chapters. Off for clients whose land use the free satellite products
 * don't describe — they see high-resolution survey data only.
 * @returns boolean indicating if satellite analytics are available to this user
 */
export function useSatelliteAnalytics(): boolean {
  return useUserFlag('satellite-analytics');
}

/**
 * Whether the site-level asset table is available: the Table tab alongside
 * Dashboard, Story and Exploration, listing the location's surveyed assets one
 * row per record. Off by default while the view is still proving out.
 * @returns boolean indicating if the site asset table is available to this user
 */
export function useSiteTableView(): boolean {
  return useUserFlag('show-table-for-site-view');
}

/**
 * Whether the pixel inspector tool is available: sampling a COG's raw band
 * values and rendered color under the cursor from the map tools rail. Off by
 * default while the feature is still rolling out.
 * @returns boolean indicating if the pixel inspector is available to this user
 */
export function usePixelInspectorFlag(): boolean {
  return useUserFlag('pixel-inspector');
}

/**
 * Whether the project dashboard is available — the readings over the map, the
 * sites and updates screens, and landing there when a project is picked.
 * @returns boolean indicating if the project dashboard is available to this user
 */
export function useProjectDashboardFlag(): boolean {
  return useUserFlag('show-ead-project-dashboard');
}

/**
 * Whether mangrove suitability analysis is available.
 * @returns boolean indicating if mangrove suitability analysis is available to this user
 */
export function useMangroveSuitability(): boolean {
  return useUserFlag('mangrove-suitability');
}

/**
 * Whether the mangrove-suitability chapter should be pushed to the front of the
 * story's chapter list instead of trailing at the end.
 * @returns boolean indicating if the mangrove chapter should be prioritised
 */
export function usePrioritiseMangroveChapter(): boolean {
  return useUserFlag('prioritise-mangrove-chapter');
}

/** Enables country-wide layers before an area of interest is drawn. */
export function useProjectLevelLayers(): boolean {
  return useUserFlag('project-level-layers');
}

/**
 * Scopes whether feature relating to regional analysis are available.
 * If a user has mangrove suitability or satellite analytics enabled, regional analysis features are available.
 * @returns boolean indicating if regional analysis is available to this user
 */
/**
 * Tenant holds one project and no monitored sites, so the map drops the
 * all-projects list and the monitored/custom choice.
 *
 * Its own flag rather than an inference from the mangrove products: a
 * full-access account holds those too, and gating on them took chrome away from
 * accounts with real projects and sites.
 */
export function useSingleProjectView(): boolean {
  return useUserFlag('single-project-view');
}

export function useRegionalAnalysis(): boolean {
  const hasMangroveSuitability = useUserFlag('mangrove-suitability');
  const hasSatelliteAnalytics = useUserFlag('satellite-analytics');

  return hasMangroveSuitability || hasSatelliteAnalytics;
}
